/* The Aesthetic Cove — the Christmas tree in the hero.
   A full fir tree in seven tiers of branches, hung with fairy lights, glass
   baubles and a champagne bead garland, turning slowly against the Warm
   Ivory wall of the hero, like a tree in a bright room at dusk. On arrival
   the studio's laser traces the garland from the foot to the tip; the lights
   switch on behind it, tier by tier, the baubles catch, and the star at the
   top bursts alight. After that the lights twinkle, a pulse of champagne
   runs up the garland now and then, the pointer tilts the tree and warms the
   lights near it, and a click or tap sends a wave of light up the branches.

   Everything is painted for a light wall: muted fir greens, baubles in muted
   red, champagne, cashmere, pearl, taupe and fir, warm-white bulbs with
   champagne halos, and plain (not additive) blending, so the glow tints the
   ivory instead of washing it to white.

   It waits for the intro to lift, draws only while it is on screen and the
   tab is visible, and a visitor who prefers reduced motion gets the finished
   tree, lit and still. One canvas: no images, no libraries. */
(() => {
  const host = document.querySelector('[data-hero-tree]');
  const canvas = host && host.querySelector('canvas');
  const ctx = canvas && canvas.getContext('2d');
  if (!ctx) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const small = Math.min(window.innerWidth, window.innerHeight) < 600;
  const TAU = Math.PI * 2;

  /* Seeded random, so the tree is dressed the same way on every visit. */
  let seed = 20261225;
  const rand = () => (seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296;
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  /* ---------- the shape, in units of the tree's height ---------- */
  const R = 0.37; // radius at the foot
  const TIERS = 7;
  // Tiers are tallest at the foot and shorten towards the tip.
  const tierPos = (u) => TIERS * (1 - Math.pow(1 - u, 0.82));
  const cone = (u) => R * Math.pow(Math.max(0, 1 - u), 1.02);
  // Each tier fans out at its foot and tucks in under the tier above.
  function radius(u) {
    const local = tierPos(u) % 1;
    return cone(u) * (0.64 + 0.42 * Math.pow(1 - local, 1.7));
  }
  // A height picked in proportion to the tree's girth there, so branches,
  // lights and baubles spread evenly over its surface.
  function sampleU(lo = 0, hi = 0.965) {
    for (let n = 0; n < 200; n++) {
      const u = lo + rand() * (hi - lo);
      if (rand() * R * 1.08 < radius(u)) return u;
    }
    return lo;
  }

  /* ---------- what hangs on it ---------- */
  const NF = small ? 330 : 600; // branch clusters
  const NL = small ? 420 : 800; // fairy lights
  const NO = small ? 22 : 34; // baubles
  const NB = small ? 230 : 400; // garland beads
  const N = NF + NL + NO + NB;
  const KIND = new Uint8Array(N); // 0 branch, 1 light, 2 bauble, 3 bead
  const U = new Float32Array(N);
  const TH = new Float32Array(N);
  const RR = new Float32Array(N);
  const SIZE = new Float32Array(N);
  const VAR = new Uint8Array(N); // sprite variant / colour
  const PH = new Float32Array(N); // twinkle phase
  const SP = new Float32Array(N); // twinkle speed
  const ON = new Float32Array(N).fill(-1); // when it lit; -1 = not yet
  let n = 0;

  for (let i = 0; i < NF; i++, n++) {
    const u = sampleU(0, 0.955);
    KIND[n] = 0;
    U[n] = u;
    TH[n] = rand() * TAU;
    RR[n] = radius(u) * (0.8 + 0.2 * Math.sqrt(rand()));
    SIZE[n] = (0.052 + 0.034 * rand()) * (0.62 + 0.55 * (1 - u));
    VAR[n] = (rand() * 4) | 0;
  }
  for (let i = 0; i < NL; i++, n++) {
    const u = sampleU(0.01, 0.95);
    const r = rand();
    KIND[n] = 1;
    U[n] = u;
    TH[n] = rand() * TAU;
    RR[n] = radius(u) * (0.97 + 0.08 * rand());
    SIZE[n] = 0.75 + 0.55 * rand();
    VAR[n] = r < 0.58 ? 0 : r < 0.8 ? 1 : r < 0.89 ? 2 : r < 0.95 ? 3 : 4; // warm white, champagne, muted red, pearl, deep champagne
    PH[n] = rand() * TAU;
    SP[n] = 0.9 + rand() * 2.6;
  }
  // Baubles hang where the branches fan out, spread round the tree by the golden angle.
  {
    const placed = [];
    for (let i = 0; i < NO; i++, n++) {
      let u = 0.5;
      let th = 0;
      for (let tries = 0; tries < 40; tries++) {
        u = sampleU(0.07, 0.86);
        // nudge to the foot of its tier, where the branch tips are
        const t0 = Math.floor(tierPos(u));
        const tu = 1 - Math.pow(1 - (t0 + 0.08 + rand() * 0.16) / TIERS, 1 / 0.82);
        u = Math.min(0.86, Math.max(0.07, tu));
        th = i * 2.39996 + (rand() - 0.5) * 0.6;
        const x = Math.cos(th) * radius(u);
        const z = Math.sin(th) * radius(u);
        if (placed.every(([px, py, pz]) => Math.hypot(px - x, py - u, pz - z) > 0.085)) break;
      }
      placed.push([Math.cos(th) * radius(u), u, Math.sin(th) * radius(u)]);
      KIND[n] = 2;
      U[n] = u;
      TH[n] = th;
      RR[n] = radius(u) * 0.99;
      SIZE[n] = (0.027 + 0.012 * rand()) * (1 - 0.3 * u);
      VAR[n] = [0, 1, 5, 3, 0, 2, 4, 1][i % 8]; // red, champagne, fir, pearl, red, cashmere, taupe, champagne
      PH[n] = rand() * TAU;
    }
  }
  // The garland: a champagne bead chain wound four and a quarter times round the tree.
  const G0 = n;
  const TURNS = 4.25;
  const garlandU = (s) => 0.05 + s * 0.87;
  for (let i = 0; i < NB; i++, n++) {
    const s = i / (NB - 1);
    const u = garlandU(s);
    KIND[n] = 3;
    U[n] = u;
    TH[n] = 0.6 + TURNS * TAU * s;
    RR[n] = cone(u) * 0.93 + 0.004;
    SIZE[n] = 1;
    PH[n] = s; // position along the garland
  }

  /* ---------- sprites, built once ---------- */
  function sheet(size, paint) {
    const c = document.createElement('canvas');
    c.width = c.height = size;
    paint(c.getContext('2d'), size);
    return c;
  }
  // A bulb: a warm-white core, the bulb's own colour, then a soft halo that
  // fades out. Drawn with plain blending, so the halo tints what is behind it.
  function glow(rgb, size = 64, core = 0.1) {
    return sheet(size, (g, s) => {
      const h = s / 2;
      const grd = g.createRadialGradient(h, h, 0, h, h, h);
      grd.addColorStop(0, 'rgba(255,250,242,1)');
      grd.addColorStop(core, 'rgba(255,250,242,0.95)');
      grd.addColorStop(core * 1.6, `rgba(${rgb},0.92)`);
      grd.addColorStop(0.3, `rgba(${rgb},0.42)`);
      grd.addColorStop(0.55, `rgba(${rgb},0.1)`);
      grd.addColorStop(1, `rgba(${rgb},0)`);
      g.fillStyle = grd;
      g.fillRect(0, 0, s, s);
    });
  }
  // Warm white, champagne, muted red, pearl, deep champagne.
  const LIGHTS = [glow('244,212,156', 64, 0.12), glow('214,178,110', 64, 0.12), glow('184,88,78', 64, 0.14), glow('232,220,200', 64, 0.12), glow('176,140,86', 64, 0.12)];
  const BEAD = glow('214,178,110', 32, 0.18);
  const HOT = glow('214,178,110', 96, 0.08);

  // A cluster of fir needles: a soft green bed with three short sprigs on top.
  // Muted, slightly greyed firs: the near side in the mid greens, the far
  // side deeper, so the tree has a shaded back against the ivory.
  function branch(variant, lit) {
    return sheet(96, (g, s) => {
      let k = variant * 7919 + (lit ? 13 : 7);
      const r = () => (k = (k * 16807) % 2147483647) / 2147483647;
      const h = s / 2;
      const bed = g.createRadialGradient(h, h, 0, h, h, s * 0.42);
      const base = lit ? '82,108,78' : '47,74,51';
      bed.addColorStop(0, `rgba(${base},0.96)`);
      bed.addColorStop(0.5, `rgba(${base},0.62)`);
      bed.addColorStop(1, `rgba(${base},0)`);
      g.fillStyle = bed;
      g.fillRect(0, 0, s, s);
      const greens = lit ? ['#4E6849', '#5B7656', '#6A8663', '#7A9873', '#8CA883'] : ['#2F4A33', '#3A5740', '#44633F', '#4E6849'];
      g.lineCap = 'round';
      for (let stem = 0; stem < 3; stem++) {
        const a = r() * TAU;
        const len = s * (0.3 + r() * 0.12);
        const x0 = h + (r() - 0.5) * s * 0.16;
        const y0 = h + (r() - 0.5) * s * 0.16;
        for (let j = 1; j <= 8; j++) {
          const t = j / 8;
          const x = x0 + Math.cos(a) * len * t;
          const y = y0 + Math.sin(a) * len * t;
          for (const side of [-1, 1]) {
            const na = a + side * (0.75 + r() * 0.35);
            const nl = s * (0.07 + r() * 0.05) * (1.1 - t * 0.5);
            g.strokeStyle = greens[(r() * greens.length) | 0];
            g.lineWidth = 1.4 + r() * 0.9;
            g.beginPath();
            g.moveTo(x, y);
            g.lineTo(x + Math.cos(na) * nl, y + Math.sin(na) * nl);
            g.stroke();
          }
        }
      }
    });
  }
  const BRANCH_LIT = [0, 1, 2, 3].map((v) => branch(v, true));
  const BRANCH_SHADE = [0, 1, 2, 3].map((v) => branch(v, false));

  // Glass baubles: lit from the upper left, a champagne rim from the lights, a
  // gold cap, and a soft taupe edge so the pale ones still read on ivory.
  function bauble(light, mid, dark) {
    return sheet(64, (g) => {
      const cx = 32;
      const cy = 35;
      const r = 24;
      const cap = g.createLinearGradient(27, 0, 37, 0);
      cap.addColorStop(0, '#8A6526');
      cap.addColorStop(0.5, '#F3D894');
      cap.addColorStop(1, '#8A6526');
      g.fillStyle = cap;
      g.fillRect(27.5, 6, 9, 7);
      const body = g.createRadialGradient(cx - 9, cy - 10, 1.5, cx, cy, r + 1);
      body.addColorStop(0, light);
      body.addColorStop(0.38, mid);
      body.addColorStop(1, dark);
      g.fillStyle = body;
      g.beginPath();
      g.arc(cx, cy, r, 0, TAU);
      g.fill();
      g.strokeStyle = 'rgba(74, 60, 51, 0.22)';
      g.lineWidth = 1;
      g.beginPath();
      g.arc(cx, cy, r - 0.5, 0, TAU);
      g.stroke();
      g.strokeStyle = 'rgba(195, 162, 110, 0.45)';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(cx, cy, r - 1.5, 0.15 * Math.PI, 0.65 * Math.PI);
      g.stroke();
      g.fillStyle = 'rgba(255,250,242,0.85)';
      g.beginPath();
      g.ellipse(cx - 9, cy - 10, 5.5, 3, -0.7, 0, TAU);
      g.fill();
    });
  }
  // Muted red, champagne, cashmere, pearl, taupe, fir.
  const BAUBLES = [
    bauble('#E3A49C', '#A94A43', '#5E2521'),
    bauble('#F7E7C6', '#C3A26E', '#6E5330'),
    bauble('#F6EEE3', '#D9C8B4', '#8B7564'),
    bauble('#FFFDF9', '#F2EBDD', '#A8988A'),
    bauble('#C4B09E', '#8B7564', '#4A3C33'),
    bauble('#A9C0A2', '#5B7656', '#2F4A33'),
  ];

  /* ---------- size ---------- */
  let dpr = 1;
  let W = 0;
  let H = 0;
  let S = 0; // tree height in canvas pixels
  let cx = 0;
  let foot = 0;
  let midY = 0;
  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return;
    W = canvas.width = Math.round(w * dpr);
    H = canvas.height = Math.round(h * dpr);
    S = Math.min(H * 0.74, W * 1.0);
    cx = W * 0.5;
    foot = H * 0.845;
    midY = foot - S * 0.5;
    if (!running) draw(0);
  }

  /* ---------- projection ---------- */
  const CAM = 2.8; // camera distance, in tree heights
  let rot = 0.4;
  let tilt = 0.12;
  const SX = new Float32Array(N);
  const SY = new Float32Array(N);
  const SK = new Float32Array(N); // perspective scale
  const SF = new Float32Array(N); // 1 = nearest the viewer, 0 = furthest
  const BUCKETS = 10;
  const bucket = Array.from({ length: BUCKETS }, () => []);
  function projectAll() {
    const ct = Math.cos(tilt);
    const st = Math.sin(tilt);
    for (let b = 0; b < BUCKETS; b++) bucket[b].length = 0;
    for (let i = 0; i < N; i++) {
      const a = TH[i] + rot;
      const r = RR[i];
      const x = r * Math.cos(a);
      const z = r * Math.sin(a);
      const y = U[i] - 0.5;
      const y2 = y * ct + z * st;
      const z2 = -y * st + z * ct;
      const k = CAM / (CAM + z2);
      SX[i] = cx + x * k * S;
      SY[i] = midY - y2 * k * S;
      SK[i] = k;
      const f = clamp01(0.5 - z2 / (2.2 * R));
      SF[i] = f;
      bucket[Math.min(BUCKETS - 1, (f * BUCKETS) | 0)].push(i);
    }
  }

  // The tip of the tree, where the star sits: on the axis, so only tilt moves it.
  const tipPoint = () => {
    const k = CAM / (CAM - 0.5 * Math.sin(tilt));
    return [cx, midY - 0.5 * Math.cos(tilt) * k * S];
  };

  /* ---------- the laser's path: the garland ---------- */
  const garlandPoint = (s) => {
    const i = G0 + Math.round(clamp01(s) * (NB - 1));
    return [SX[i], SY[i]];
  };

  /* ---------- timeline ---------- */
  const DRAW = small ? 2.6 : 3.1; // seconds for the laser to reach the tip
  let running = false;
  let raf = 0;
  let last = 0;
  let t = 0;
  let startAt = reduce ? -100 : null;
  let doneAt = reduce ? -100 : null;
  let head = reduce ? 1 : 0; // how far up the garland the laser has come (0..1)
  let litU = reduce ? 1.2 : -0.2; // everything below this height is lit
  let rippleAt = -100;
  let chaseAt = 4;
  const sparks = [];
  let glint = { at: -10, i: -1 };
  if (reduce) for (let i = 0; i < N; i++) ON[i] = -100;

  /* ---------- pointer ---------- */
  let aimRot = 0;
  let aimTilt = 0;
  let offRot = 0;
  let offTilt = 0;
  let px = -1e4;
  let py = -1e4;
  if (finePointer && !reduce) {
    window.addEventListener(
      'pointermove',
      (e) => {
        aimRot = (e.clientX / window.innerWidth - 0.5) * 0.8;
        aimTilt = (e.clientY / window.innerHeight - 0.5) * 0.12;
        const r = canvas.getBoundingClientRect();
        px = (e.clientX - r.left) * dpr;
        py = (e.clientY - r.top) * dpr;
      },
      { passive: true }
    );
    host.addEventListener('pointerleave', () => {
      px = py = -1e4;
    });
  }
  if (!reduce) {
    host.addEventListener('pointerdown', () => {
      rippleAt = t;
      if (doneAt !== null) {
        const [tx, ty] = tipPoint();
        burst(tx, ty - S * 0.03, 22);
      }
    });
  }

  /* ---------- drawing ---------- */
  function backdrop() {
    // a soft taupe shadow where the tree stands, then a warm pool of
    // champagne light over it once the lights are on
    ctx.save();
    ctx.translate(cx, foot);
    ctx.scale(1, 0.16);
    const r = S * 0.7;
    const shade = ctx.createRadialGradient(0, 0, 0, 0, 0, r * 0.55);
    shade.addColorStop(0, 'rgba(139, 117, 100, 0.2)');
    shade.addColorStop(1, 'rgba(139, 117, 100, 0)');
    ctx.fillStyle = shade;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, TAU);
    ctx.fill();
    const grd = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    grd.addColorStop(0, `rgba(195, 162, 110, ${(0.3 * clamp01(litU + 0.2)).toFixed(3)})`);
    grd.addColorStop(0.5, `rgba(195, 162, 110, ${(0.09 * clamp01(litU + 0.2)).toFixed(3)})`);
    grd.addColorStop(1, 'rgba(195, 162, 110, 0)');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, TAU);
    ctx.fill();
    ctx.restore();
    // and a champagne halo on the wall round the whole tree once it is lit
    if (litU > 0) {
      const a = 0.16 * clamp01(litU);
      const grd2 = ctx.createRadialGradient(cx, midY + S * 0.12, 0, cx, midY + S * 0.12, S * 0.62);
      grd2.addColorStop(0, `rgba(195, 162, 110, ${a.toFixed(3)})`);
      grd2.addColorStop(1, 'rgba(195, 162, 110, 0)');
      ctx.fillStyle = grd2;
      ctx.fillRect(0, 0, W, H);
    }
  }

  // How lit a height is: dim before the laser passes, full a little after.
  const lit = (u) => clamp01((litU - u) / 0.07);

  // Before its lights come on a tier is a pale ghost of itself on the wall.
  function drawBranch(i) {
    const k = SK[i];
    const s = SIZE[i] * S * k * 1.9;
    const f = SF[i];
    const l = lit(U[i]);
    ctx.globalAlpha = (0.38 + 0.62 * l) * (0.38 + 0.62 * f);
    const img = (f > 0.42 ? BRANCH_LIT : BRANCH_SHADE)[VAR[i]];
    ctx.drawImage(img, SX[i] - s / 2, SY[i] - s / 2, s, s);
  }

  function drawBauble(i) {
    if (ON[i] === -1) return;
    const age = t - ON[i];
    const pop = age < 0.5 ? 1 + 0.35 * Math.sin(Math.min(1, age / 0.5) * Math.PI) * (1 - age / 0.5) : 1;
    const grow = Math.min(1, age / 0.18);
    const s = SIZE[i] * S * SK[i] * 2.55 * pop * grow;
    ctx.globalAlpha = 0.5 + 0.5 * SF[i];
    // a thread up to the branch, in deep champagne so it shows on the ivory
    ctx.strokeStyle = 'rgba(138, 106, 46, 0.6)';
    ctx.lineWidth = Math.max(1, dpr * 0.8);
    ctx.beginPath();
    ctx.moveTo(SX[i], SY[i] - s * 0.42);
    ctx.lineTo(SX[i], SY[i] - s * 0.62);
    ctx.stroke();
    ctx.drawImage(BAUBLES[VAR[i]], SX[i] - s / 2, SY[i] - s * 0.45, s, s);
  }

  function lightBoost(i, time) {
    let boost = 1;
    if (time - rippleAt < 2.2) {
      const d = U[i] - (time - rippleAt) * 0.75;
      boost += 1.5 * Math.exp(-(d * d) / 0.005);
    }
    if (px > -1e3) {
      const dx = SX[i] - px;
      const dy = SY[i] - py;
      const d2 = dx * dx + dy * dy;
      const rad = S * 0.12;
      if (d2 < rad * rad) boost += 0.9 * (1 - Math.sqrt(d2) / rad);
    }
    return boost;
  }

  function drawLight(i, time) {
    if (ON[i] === -1) return;
    const age = time - ON[i];
    const flash = age < 0.7 ? 1 + 1.6 * Math.exp(-age / 0.16) : 1;
    const tw = 0.62 + 0.38 * Math.sin(time * SP[i] + PH[i]) * Math.sin(time * SP[i] * 0.37 + PH[i] * 2);
    const f = SF[i];
    const boost = Math.min(2.4, lightBoost(i, time));
    const s = S * 0.027 * SIZE[i] * SK[i] * (0.6 + 0.55 * f) * (0.9 + 0.1 * tw) * Math.min(1.7, boost);
    ctx.globalAlpha = Math.min(1, (0.3 + 0.7 * f) * (0.6 + 0.4 * tw) * flash * boost);
    ctx.drawImage(LIGHTS[VAR[i]], SX[i] - s, SY[i] - s, s * 2, s * 2);
  }

  function drawBead(i, time) {
    if (ON[i] === -1) return;
    const s0 = PH[i];
    let a = 0.55;
    const chase = time - chaseAt;
    if (chase >= 0 && chase < 1.8) {
      const d = s0 - chase / 1.4;
      a += 1.3 * Math.exp(-(d * d) / 0.0016);
    }
    const age = time - ON[i];
    if (age < 0.5) a += 1.2 * (1 - age / 0.5);
    const f = SF[i];
    const s = S * 0.0115 * SK[i] * (0.6 + 0.6 * f);
    ctx.globalAlpha = Math.min(1, a * (0.3 + 0.7 * f));
    ctx.drawImage(BEAD, SX[i] - s, SY[i] - s, s * 2, s * 2);
  }

  // Lights and beads are painted over the branches with plain blending (not
  // additive, which would wash the ivory to white): the bulbs read as bright
  // points on the green, and their halos warm what is behind them.
  function drawTree(time) {
    ctx.globalCompositeOperation = 'source-over';
    for (let b = 0; b < BUCKETS; b++) {
      const list = bucket[b];
      for (let j = 0; j < list.length; j++) if (KIND[list[j]] === 0) drawBranch(list[j]);
      for (let j = 0; j < list.length; j++) if (KIND[list[j]] === 2) drawBauble(list[j]);
      for (let j = 0; j < list.length; j++) {
        const i = list[j];
        if (KIND[i] === 1) drawLight(i, time);
        else if (KIND[i] === 3) drawBead(i, time);
      }
    }
    ctx.globalAlpha = 1;
  }

  // Now and then a bauble near the front catches the light: a four-point glint.
  function glints(time) {
    if (doneAt === null || reduce) return;
    if (time - glint.at > 1.6 && rand() < 0.03) {
      const front = [];
      for (let i = NF + NL; i < NF + NL + NO; i++) if (SF[i] > 0.62) front.push(i);
      if (front.length) glint = { at: time, i: front[(rand() * front.length) | 0] };
    }
    const age = time - glint.at;
    if (glint.i < 0 || age > 0.8) return;
    const a = Math.sin((age / 0.8) * Math.PI);
    const i = glint.i;
    const s = SIZE[i] * S * SK[i] * 2.55;
    const x = SX[i] - s * 0.16;
    const y = SY[i] - s * 0.2;
    const len = S * 0.045 * a;
    ctx.strokeStyle = `rgba(255, 250, 242, ${(0.9 * a).toFixed(3)})`;
    ctx.lineWidth = Math.max(1, dpr);
    ctx.beginPath();
    ctx.moveTo(x - len, y);
    ctx.lineTo(x + len, y);
    ctx.moveTo(x, y - len);
    ctx.lineTo(x, y + len);
    ctx.stroke();
    const hs = len * 0.5;
    ctx.globalAlpha = a * 0.8;
    ctx.drawImage(HOT, x - hs, y - hs, hs * 2, hs * 2);
    ctx.globalAlpha = 1;
  }

  function starPath(x, y, r) {
    ctx.beginPath();
    for (let k = 0; k < 10; k++) {
      const a = (k * TAU) / 10 - Math.PI / 2;
      const rr = k % 2 ? r * 0.42 : r;
      const sx = x + Math.cos(a) * rr;
      const sy = y + Math.sin(a) * rr;
      if (k) ctx.lineTo(sx, sy);
      else ctx.moveTo(sx, sy);
    }
    ctx.closePath();
  }

  function star(age, time) {
    const [x, top] = tipPoint();
    const r = S * 0.052;
    const y = top - r * 0.55;
    const g = reduce ? 1 : Math.min(1, age / 0.7);
    const back = 1.70158;
    const q = g - 1;
    const scale = reduce ? 1 : 1 + (back + 1) * q * q * q + back * q * q;
    const pulse = 0.86 + 0.14 * Math.sin(time * 1.7);
    const flare = reduce ? 0 : Math.max(0, 1 - age / 1.2);

    // a champagne halo on the wall and six slow rays
    const halo = r * (4.2 + flare * 5) * Math.max(0.01, scale);
    ctx.globalAlpha = Math.min(1, (0.8 * pulse + flare) * g);
    ctx.drawImage(HOT, x - halo, y - halo, halo * 2, halo * 2);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(time * 0.06);
    for (let k = 0; k < 6; k++) {
      const len = r * (k % 2 ? 2.4 : 4.6) * (1 + flare * 1.6) * Math.max(0.01, scale);
      const grd = ctx.createLinearGradient(-len, 0, len, 0);
      grd.addColorStop(0, 'rgba(195,162,110,0)');
      grd.addColorStop(0.5, `rgba(195,162,110,${(0.6 * g * pulse).toFixed(3)})`);
      grd.addColorStop(1, 'rgba(195,162,110,0)');
      ctx.strokeStyle = grd;
      ctx.lineWidth = Math.max(1, r * 0.04);
      ctx.beginPath();
      ctx.moveTo(-len, 0);
      ctx.lineTo(len, 0);
      ctx.stroke();
      ctx.rotate(Math.PI / 6);
    }
    ctx.restore();
    ctx.globalAlpha = 1;

    // the star itself: champagne gold, deep at the lower points
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    const fill = ctx.createLinearGradient(-r, -r, r, r);
    fill.addColorStop(0, '#FFF6DA');
    fill.addColorStop(0.45, '#D9B878');
    fill.addColorStop(1, '#8A6A2E');
    starPath(0, 0, r);
    ctx.fillStyle = fill;
    ctx.shadowColor = 'rgba(195, 162, 110, 0.85)';
    ctx.shadowBlur = r * 0.8;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.lineWidth = Math.max(1, r * 0.035);
    ctx.strokeStyle = 'rgba(138, 106, 46, 0.6)';
    ctx.stroke();
    ctx.restore();
  }

  function laser(time, dt) {
    const [x, y] = garlandPoint(head);
    const flicker = 0.85 + 0.15 * Math.sin(time * 61) * Math.sin(time * 23);
    // a champagne beam down from the top of the band, a warm-white point
    // ringed in deep champagne where it burns
    const beam = ctx.createLinearGradient(x, 0, x, y);
    beam.addColorStop(0, 'rgba(195, 162, 110, 0)');
    beam.addColorStop(0.75, `rgba(195, 162, 110, ${(0.3 * flicker).toFixed(3)})`);
    beam.addColorStop(1, `rgba(176, 140, 86, ${(0.85 * flicker).toFixed(3)})`);
    ctx.strokeStyle = beam;
    ctx.lineWidth = Math.max(1, 1.2 * dpr);
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.lineWidth = 7 * dpr;
    ctx.globalAlpha = 0.14;
    ctx.stroke();
    ctx.globalAlpha = 1;
    const hs = S * 0.055 * flicker;
    ctx.drawImage(HOT, x - hs, y - hs, hs * 2, hs * 2);
    ctx.fillStyle = '#FFF6EC';
    ctx.strokeStyle = 'rgba(138, 106, 46, 0.9)';
    ctx.lineWidth = Math.max(1, dpr);
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1.4, 1.8 * dpr), 0, TAU);
    ctx.fill();
    ctx.stroke();
    const count = Math.min(6, Math.floor(dt * 120 + rand()));
    for (let k = 0; k < count && sparks.length < 160; k++) {
      const a = -Math.PI / 2 + (rand() - 0.5) * 2.6;
      const v = S * (0.22 + rand() * 0.6);
      sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, age: 0, life: 0.35 + rand() * 0.5, gold: true });
    }
  }

  function burst(x, y, count) {
    for (let k = 0; k < count && sparks.length < 200; k++) {
      const a = rand() * TAU;
      const v = S * (0.25 + rand() * 0.55);
      sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - S * 0.2, age: 0, life: 0.6 + rand() * 0.7, gold: rand() < 0.8 });
    }
  }

  // Sparks: champagne, bright while young and deepening as they cool; the
  // odd one from the star's burst is a fleck of muted red.
  function updateSparks(dt) {
    if (!sparks.length) return;
    const g = S * 1.5;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1, 1.3 * dpr);
    for (let k = sparks.length - 1; k >= 0; k--) {
      const p = sparks[k];
      p.age += dt;
      if (p.age >= p.life) {
        sparks.splice(k, 1);
        continue;
      }
      p.vy += g * dt;
      p.vx *= 1 - 0.8 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const fade = 1 - p.age / p.life;
      ctx.strokeStyle = p.gold
        ? `rgba(${Math.round(140 + 74 * fade)}, ${Math.round(106 + 72 * fade)}, ${Math.round(46 + 64 * fade)}, ${fade.toFixed(3)})`
        : `rgba(169, 74, 67, ${fade.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(p.x - p.vx * 0.02, p.y - p.vy * 0.02);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
  }

  function draw(dt) {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, H);
    if (!S) return;
    projectAll();

    // the laser climbs the garland; everything below it switches on
    if (startAt !== null && doneAt === null) {
      const p = Math.min(1, (t - startAt) / DRAW);
      head = 0.5 - 0.5 * Math.cos(Math.PI * p);
      litU = garlandU(head) + 0.015;
      if (p >= 1) {
        doneAt = t;
        litU = 1.2;
        const [tx, ty] = tipPoint();
        burst(tx, ty - S * 0.03, 36);
        chaseAt = t + 1.2;
      }
    }
    if (startAt !== null) {
      for (let i = NF; i < N; i++) if (ON[i] === -1 && U[i] <= litU) ON[i] = t;
    }

    backdrop();
    drawTree(t);
    glints(t);
    if (doneAt !== null) star(t - doneAt, t);
    if (startAt !== null && doneAt === null) laser(t, dt);
    updateSparks(dt);
  }

  /* ---------- loop ---------- */
  // On a touch screen the tree holds still while a finger is scrolling the
  // page and carries on the moment it stops, so the scroll gets the phone's
  // full attention. With a mouse it keeps turning (desktops have the room).
  let scrollHold = 0;
  if (!finePointer) window.addEventListener('scroll', () => (scrollHold = performance.now() + 160), { passive: true });
  function frame(now) {
    raf = requestAnimationFrame(frame);
    if (now < scrollHold) {
      last = now;
      return;
    }
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
    last = now;
    t += dt;
    offRot += (aimRot - offRot) * 0.04;
    offTilt += (aimTilt - offTilt) * 0.04;
    rot = 0.4 + t * 0.13 + offRot;
    tilt = Math.min(0.26, Math.max(0.04, 0.12 + offTilt));
    if (doneAt !== null && t > chaseAt + 1.8) chaseAt = t + 4 + rand() * 3;
    draw(dt);
  }
  function play() {
    if (running || reduce) return;
    running = true;
    last = 0;
    raf = requestAnimationFrame(frame);
  }
  function pause() {
    running = false;
    cancelAnimationFrame(raf);
  }

  // Draw only while a good part of the tree is in view: once it is mostly
  // scrolled away it holds still, which frees the frame for the scroll.
  let visible = true;
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.3;
        if (visible && !document.hidden) play();
        else pause();
      },
      { threshold: [0, 0.3, 0.6] }
    ).observe(host);
  }
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause();
    else if (visible) play();
  });

  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(host);
  else window.addEventListener('resize', resize);
  resize();

  if (reduce) {
    draw(0);
    return;
  }

  // The laser starts once the intro has lifted (or straight away without it).
  const begin = () => {
    if (startAt === null) startAt = t;
  };
  if (document.documentElement.classList.contains('intro-active')) {
    window.addEventListener('intro:done', () => setTimeout(begin, 120), { once: true });
    setTimeout(begin, 12000);
  } else {
    setTimeout(begin, 450);
  }
  play();
})();
